import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, FormField } from '@chikara/ui';
import { t } from '@/i18n';
import styles from './UserFormModal.module.css';
import type { TeamMemberRole } from '../../types/team.types';

export interface InputFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string;
  type?: 'text' | 'email' | 'tel';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  error?: string;
  disabled?: boolean;
  hint?: string;
  hintVariant?: 'default' | 'error';
  maxLength?: number;
  onBlur?: () => void;
}

export function InputField({
  value,
  onChange,
  placeholder,
  label,
  type = 'text',
  inputMode,
  error,
  disabled = false,
  hint,
  hintVariant,
  maxLength,
  onBlur,
}: InputFieldProps) {
  return (
    <div className={styles.inputWrapper}>
      <FormField
        label={label ?? placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        hint={hint}
        hintVariant={hintVariant}
        type={type}
        inputMode={inputMode}
        disabled={disabled}
        placeholder={label ? placeholder : undefined}
        maxLength={maxLength}
        onBlur={onBlur}
      />
    </div>
  );
}

export interface ReadonlyFieldProps {
  label: string;
  value: string | number;
  variant?: 'default' | 'muted';
}

export function ReadonlyField({ label, value, variant = 'default' }: ReadonlyFieldProps) {
  return (
    <div className={styles.fieldGroup}>
      <label className={styles.fieldLabel}>{label}</label>
      <div className={variant === 'muted' ? styles.fieldValueMuted : styles.fieldValue}>
        {value}
      </div>
    </div>
  );
}

export interface FieldRowProps {
  children: ReactNode;
}

export function FieldRow({ children }: FieldRowProps) {
  return <div className={styles.fieldRow}>{children}</div>;
}

export interface SectionTitleProps {
  children: ReactNode;
}

export function SectionTitle({ children }: SectionTitleProps) {
  return <h3 className={styles.sectionTitle}>{children}</h3>;
}

export interface TypeSelectorProps {
  label: string;
  value: TeamMemberRole;
  onChange: (value: TeamMemberRole) => void;
  disabled?: boolean;
}

export function TypeSelector({ label, value, onChange, disabled = false }: TypeSelectorProps) {
  return (
    <div className={`${styles.typeSelector} ${disabled ? styles.disabled : ''}`}>
      <span className={styles.typeSelectorLabel}>{label}</span>
      <div className={styles.typeOptions} role="group" aria-label={label}>
        <button
          type="button"
          className={styles.typeChip}
          data-selected={value === 'ADMIN'}
          onClick={() => onChange('ADMIN')}
          aria-pressed={value === 'ADMIN'}
          disabled={disabled}
        >
          Admin
        </button>
        <button
          type="button"
          className={styles.typeChip}
          data-selected={value === 'BASIC'}
          onClick={() => onChange('BASIC')}
          aria-pressed={value === 'BASIC'}
          disabled={disabled}
        >
          Básico
        </button>
      </div>
    </div>
  );
}

export interface DeactivateLinkProps {
  onClick: () => void;
  disabled?: boolean;
}

export function DeactivateLink({ onClick, disabled = false }: DeactivateLinkProps) {
  return (
    <button
      type="button"
      className={styles.deactivateLink}
      onClick={onClick}
      disabled={disabled}
    >
      <DeactivateIcon />
      {t('team.modals.editUser.deactivate')}
    </button>
  );
}

function DeactivateIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ConfirmModal({
  isOpen,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <div className={styles.confirmOverlay}>
      <div className={styles.confirmModal} role="alertdialog" aria-modal="true">
        <h3 className={styles.confirmTitle}>{title}</h3>
        <p className={styles.confirmDescription}>{description}</p>
        <div className={styles.confirmActions}>
          <Button
            variant="primary"
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isLoading}
            fullWidth
          >
            {confirmLabel}
          </Button>
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            fullWidth
          >
            {cancelLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
